import './styles.scss';
import ICustomInputProps, { Types } from './ICustomInputProps';
import { ReactComponent as SpanIcon } from '../../assets/svg/spam.svg';
import {
    GetCnpjSettings,
    GetDefaultSettings,
    GetInputDecimalWithTwoPlaces,
    GetInputNumberSettings,
    GetLoadInput,
    GetPhoneSettings,
} from './settings/input-settings';
import { MASKTYPE, SIZE, TYPE } from './input-constants';
import Icon from '../icon';

function Input({
    id,
    type,
    label,
    name,
    mask,
    placeholder,
    maxlength,
    value,
    sizeType,
    onChange,
    error,
    helperText,
    counterEnabled,
    dataTestId,
    disabled,
    className,
    hidden,
    onKeyDown,
    onPaste,
    icon,
}: Readonly<Partial<ICustomInputProps>>): JSX.Element | null {
    className = className ?? '';
    sizeType = sizeType ?? SIZE.LG;
    type = type ?? TYPE.TEXT;
    value = value ?? '';
    hidden = hidden ?? false;

    let charsCounter = 0;
    let config = GetDefaultSettings();

    const selectSettings = (type: Types) => {
        if (type === TYPE.NUMBER) {
            config = GetInputNumberSettings(maxlength);
            return;
        }

        switch (mask) {
            case MASKTYPE.CNPJ:
                config = GetCnpjSettings();
                break;
            case MASKTYPE.PHONE:
                config = GetPhoneSettings();
                break;
            case MASKTYPE.DECIMAL_SMALL:
                config = GetInputDecimalWithTwoPlaces();
                break;
            case MASKTYPE.LOADS:
                config = GetLoadInput();
                break;
            default:
                break;
        }

        maxlength = maxlength ?? config.maxlength;
    };

    const setCharsCounter = (value: any) => {
        charsCounter = value.toString().length;
    };

    selectSettings(type);
    setCharsCounter(value);

    const getLabelClasses = () => {
        let style = `input-base-label input-base-label--${sizeType} `;

        if (disabled) {
            style += 'input-base-label--disabled';
        }

        return style;
    };

    const getInputClasses = () => {
        let style = `input-base-input input-base-input--${sizeType} `;

        if (error && helperText) {
            style += 'input-base-input--error';
        }

        if (icon) {
            style += 'input-base-input--with-icon';
        }

        return style;
    };

    const getHelperClasses = () => {
        let style = 'input-base-helper ';

        if (error && helperText) {
            style += 'input-base-helper--error ';
        }

        if (disabled) {
            style += 'input-base-label--disabled';
        }

        return style;
    };

    const renderMsgError = () => {
        return (
            <>
                <SpanIcon />
                {helperText}
            </>
        );
    };

    const handlePaste = (event: any) => {
        if (onPaste) {
            onPaste(event);
            return;
        }

        if (config.onPaste) {
            config.onPaste(event);
        }
    };

    if (hidden) return null;

    return (
        <div className={`input-base ${className}`}>
            <label htmlFor={id} className={getLabelClasses()}>
                {label}
            </label>
            <div className="input-base-container">
                {icon && (
                    <Icon className="input-base-icon" size="md" icon={icon} />
                )}
                <input
                    id={id}
                    type={type}
                    name={name}
                    className={getInputClasses()}
                    disabled={disabled}
                    placeholder={placeholder}
                    onBeforeInput={config.onBeforeInput}
                    value={config.getValue(value)}
                    maxLength={maxlength}
                    aria-label={label}
                    onPaste={handlePaste}
                    onChange={(e: any) => (onChange ? onChange(e) : undefined)}
                    onKeyDown={(e: any) =>
                        onKeyDown ? onKeyDown(e) : undefined
                    }
                    data-testid={dataTestId}
                    min={config.minValue}
                    hidden={hidden}
                />
            </div>
            <span className={getHelperClasses()}>
                {error && renderMsgError()}
                {counterEnabled && (
                    <span id="textarea-counter">
                        {charsCounter}/{maxlength}
                    </span>
                )}
            </span>
        </div>
    );
}

export default Input;
